import * as THREE from 'three';
import { commonGLSL, curvatureGLSL, lightingGLSL, terrainVisGLSL } from '../shaders/common';
import { shadowGLSL } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';

/** Instanced PBR material with albedo / normal / roughness maps (derivative TBN), baked AO in COLOR_0.r. */
const inst = /* glsl */ `
in vec4 aInst0;
in vec4 aInst1;
in vec4 color;
mat3 rotY(float a) { float c = cos(a), s = sin(a); return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c); }
`;

const vert = /* glsl */ `
out vec3 vWorld;
out vec3 vNormal;
out vec2 vUv;
out float vAO;
void main() {
  mat3 R = rotY(aInst0.w);
  vec3 sp = position * aInst1.xyz;
  vec3 wp = R * sp + aInst0.xyz;
  wp.y += sp.x * aInst1.w; // shear along local x (follow the slope)
  vNormal = normalize(R * (normal / aInst1.xyz));
  vWorld = wp;
  vUv = uv * uUvScale;
  vAO = color.r;
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const depthVert = /* glsl */ `
void main() {
  mat3 R = rotY(aInst0.w);
  vec3 sp = position * aInst1.xyz;
  vec3 wp = R * sp + aInst0.xyz;
  wp.y += sp.x * aInst1.w;
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const frag = /* glsl */ `
uniform sampler2D uMap;
uniform sampler2D uNormalMap;
uniform sampler2D uRoughMap;
uniform float uHasMaps;
uniform float uHasMapOnly;
uniform vec3 uTint;
uniform float uMetal;
in vec3 vWorld;
in vec3 vNormal;
in vec2 vUv;
in float vAO;
layout(location = 0) out vec4 fragColor;
mat3 cotangentFrame(vec3 N, vec3 p, vec2 uv) {
  vec3 dp1 = dFdx(p), dp2 = dFdy(p);
  vec2 duv1 = dFdx(uv), duv2 = dFdy(uv);
  vec3 dp2perp = cross(dp2, N), dp1perp = cross(N, dp1);
  vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
  vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;
  float invmax = inversesqrt(max(dot(T, T), dot(B, B)) + 1e-12);
  return mat3(T * invmax, B * invmax, N);
}
void main() {
  vec3 N = normalize(vNormal);
  if (!gl_FrontFacing) N = -N;
  vec3 alb = uTint;
  float rough = 0.8;
  if (uHasMaps > 0.5) {
    alb *= texture(uMap, vUv).rgb;
    vec3 tn = texture(uNormalMap, vUv).xyz * 2.0 - 1.0;
    tn.y = -tn.y; // glTF UV convention without tangents (matches GLTFLoader normalScale (1,-1))
    N = normalize(cotangentFrame(N, vWorld, vUv) * tn);
    rough = texture(uRoughMap, vUv).r;
  } else if (uHasMapOnly > 0.5) {
    alb *= texture(uMap, vUv).rgb;
    rough = 0.7;
  }
  float snow = smoothstep(mix(1.05, 0.5, uSnowCover), mix(1.05, 0.5, uSnowCover) + 0.1, N.y) * step(0.001, uSnowCover);
  alb = mix(alb * mix(1.0, 0.65, uWetness), vec3(0.9, 0.91, 0.94), snow);
  rough = mix(mix(rough, 0.35, uWetness), 0.55, snow);
  vec3 V = normalize(cameraPosition - vWorld);
  float sh = getShadow(vWorld, normalize(vNormal), gl_FragCoord.xy);
  float f0 = mix(0.04, 0.5, uMetal);
  float tv = terrainSkyVis(vWorld);
  vec3 col = shadeSurface(alb * (1.0 - uMetal * 0.5), N, V, vWorld, rough, f0, sh, tv, mix(0.4, 1.0, vAO), vec3(0.55, 0.32, 0.2), 0.0);
  fragColor = vec4(col, 1.0);
}
`;

export function createPropMaterial(opts: { map?: THREE.Texture; normal?: THREE.Texture; rough?: THREE.Texture; tint?: THREE.Color; metal?: number; uvScale?: number },
  shadowUniforms: Record<string, THREE.IUniform>) {
  const has = !!(opts.map && opts.normal && opts.rough);
  const local = {
    uMap: { value: opts.map ?? null },
    uNormalMap: { value: opts.normal ?? null },
    uRoughMap: { value: opts.rough ?? null },
    uHasMaps: { value: has ? 1 : 0 },
    uHasMapOnly: { value: !has && opts.map ? 1 : 0 },
    uTint: { value: opts.tint ?? new THREE.Color(1, 1, 1) },
    uMetal: { value: opts.metal ?? 0 },
    uUvScale: { value: opts.uvScale ?? 1 },
  };
  const material = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + 'uniform float uUvScale;\n' + inst + vert,
    fragmentShader: commonGLSL + lightingGLSL + shadowGLSL + terrainVisGLSL + frag,
    uniforms: { ...shared, ...shadowUniforms, ...local },
    side: THREE.DoubleSide,
  });
  const depthMaterial = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + inst + depthVert,
    fragmentShader: 'layout(location = 0) out vec4 fragColor; void main() { fragColor = vec4(1.0); }',
    uniforms: { ...shared },
    side: THREE.DoubleSide,
  });
  return { material, depthMaterial };
}
